/**
 * @file Inline banner explaining offline / unsynced / stale-list states.
 */

import React from 'react';
import { CloudOff, Loader, RefreshCw, AlertTriangle } from 'lucide-react';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Surfaces the states that used to fail silently on View Bidders: a dropped
 * connection, pick changes still waiting to reach the server, and a bidder
 * list that is showing cached rows because the last refresh failed.
 *
 * @param {object} props
 * @param {boolean} props.isOnline
 * @param {number} props.pendingCount Pick changes saved locally but not confirmed.
 * @param {boolean} props.syncing
 * @param {string|null} [props.syncError]
 * @param {boolean} [props.listStale] Cached rows shown after a failed refresh.
 * @param {() => void} props.onRetrySync
 * @param {() => void} props.onRetryList
 */
function ConnectionStatusBar({
  isOnline,
  pendingCount,
  syncing,
  syncError,
  listStale,
  onRetrySync,
  onRetryList,
}) {
  const messages = [];

  if (!isOnline) {
    messages.push({
      key: 'offline',
      tone: 'warning',
      icon: <CloudOff size={16} aria-hidden />,
      text:
        pendingCount > 0
          ? `Offline — ${plural(pendingCount, 'change')} saved on this device. They'll sync automatically when you're back online.`
          : "Offline — you can keep marking bidders. Changes will sync when you're back online.",
    });
  } else if (syncing && pendingCount > 0) {
    messages.push({
      key: 'syncing',
      tone: 'info',
      icon: <Loader className="spin" size={16} aria-hidden />,
      text: `Syncing ${plural(pendingCount, 'change')}…`,
    });
  } else if (pendingCount > 0) {
    messages.push({
      key: 'pending',
      tone: 'warning',
      icon: <AlertTriangle size={16} aria-hidden />,
      text: syncError
        ? `Couldn't sync ${plural(pendingCount, 'change')}. ${syncError}`
        : `${plural(pendingCount, 'change')} waiting to sync.`,
      action: { label: 'Retry now', onClick: onRetrySync },
    });
  } else if (syncError) {
    // Nothing left queued, so the write was refused rather than delayed.
    messages.push({
      key: 'rejected',
      tone: 'warning',
      icon: <AlertTriangle size={16} aria-hidden />,
      text: `A pick change was not saved. ${syncError}`,
      action: { label: 'Reload list', onClick: onRetrySync },
    });
  }

  if (listStale) {
    messages.push({
      key: 'stale',
      tone: 'warning',
      icon: <AlertTriangle size={16} aria-hidden />,
      text: "Showing the last loaded list — couldn't reach the server to refresh it.",
      action: { label: 'Refresh', onClick: onRetryList },
    });
  }

  if (messages.length === 0) return null;

  return (
    <div className="connection-status" role="status" aria-live="polite">
      {messages.map((message) => (
        <div key={message.key} className={`connection-status-row connection-status-${message.tone}`}>
          {message.icon}
          <span>{message.text}</span>
          {message.action ? (
            <button
              type="button"
              className="connection-status-action"
              onClick={message.action.onClick}
            >
              <RefreshCw size={13} aria-hidden />
              {message.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default ConnectionStatusBar;
